import { FunctionComponent, useState } from 'react'
import { Alert, Button, Form, Spinner } from 'react-bootstrap'

import * as userApi from 'features/user/api.client'
import { getLoggedUser, setLoggedUser } from 'features/user/auth'
import { formatCpf, validateCpf } from 'utilities/cpf.util'
import {
  formatPhoneNumber,
  validatePhoneNumber,
} from 'utilities/phoneNumber.util'
import { isValidEmail } from 'utilities/email.util'
import { AppMessage } from 'utilities/messages'
import { getApiErrorMessage } from '../apiError'
import { useDoctorData } from '../useDoctorData'
import AccountSection from './accountSection'
import accountCss from '../account.module.scss'

type ProfileSectionProps = {
  initialName: string
  initialEmail: string
  isDoctor: boolean
}

/**
 * Name and email (users endpoint) plus, for doctors, CPF and phone
 * (doctors endpoint). Both are saved by the same button.
 */
const ProfileSection: FunctionComponent<ProfileSectionProps> = ({
  initialName,
  initialEmail,
  isDoctor,
}) => {
  const [name, setName] = useState(initialName)
  const [email, setEmail] = useState(initialEmail)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<AppMessage>({ show: false })
  const doctorData = useDoctorData(isDoctor)
  const { doctor, setDoctor } = doctorData

  const nameValid = name.trim().length > 0
  const emailValid = isValidEmail(email.trim())
  const cpfValid = validateCpf(formatCpf(doctor.cpf))
  const phoneValid = validatePhoneNumber(doctor.phoneNumber)
  const canSave =
    nameValid && emailValid && (!isDoctor || doctorData.isValid) && !saving

  const save = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSaving(true)
    setMessage({ show: false })

    try {
      const user = await userApi.updateMe({
        name: name.trim(),
        email: email.trim(),
      })
      setLoggedUser({ ...getLoggedUser(), ...user })
    } catch (error) {
      setMessage({
        show: true,
        success: false,
        message: getApiErrorMessage(error, 'Erro ao atualizar os dados.'),
      })
      setSaving(false)
      return
    }

    if (isDoctor) {
      try {
        await doctorData.save()
      } catch (error) {
        setMessage({
          show: true,
          success: false,
          message: getApiErrorMessage(
            error,
            'Nome e email salvos, mas houve um erro ao salvar CPF e telefone.'
          ),
        })
        setSaving(false)
        return
      }
    }

    setMessage({ show: true, success: true, message: 'Dados atualizados.' })
    setSaving(false)
  }

  return (
    <AccountSection icon="user" title="Dados pessoais">
      {isDoctor && !doctorData.created && !doctorData.loadError && (
        <Alert variant="warning">
          Informe seu CPF e telefone para poder solicitar acesso aos repasses.
        </Alert>
      )}
      {doctorData.loadError && (
        <Alert variant="danger">Erro ao buscar os dados do médico.</Alert>
      )}
      {message.show && (
        <Alert
          variant={message.success ? 'success' : 'danger'}
          onClose={() => setMessage({ show: false })}
          dismissible
        >
          {message.message}
        </Alert>
      )}

      <Form noValidate onSubmit={save}>
        <div className="form-grid form-grid-2">
          <Form.Group controlId="account-name">
            <Form.Label>Nome</Form.Label>
            <Form.Control
              value={name}
              maxLength={100}
              autoComplete="name"
              isInvalid={!nameValid}
              onChange={(event) => setName(event.currentTarget.value)}
            />
            <Form.Control.Feedback type="invalid">Informe o nome.</Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="account-email">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              value={email}
              maxLength={200}
              autoComplete="email"
              isInvalid={!emailValid}
              onChange={(event) => setEmail(event.currentTarget.value)}
            />
            <Form.Control.Feedback type="invalid">
              Informe um email válido.
            </Form.Control.Feedback>
          </Form.Group>

          {isDoctor && (
            <>
              <Form.Group controlId="account-cpf">
                <Form.Label>CPF</Form.Label>
                <Form.Control
                  placeholder="000.000.000-00"
                  value={formatCpf(doctor.cpf)}
                  isInvalid={doctor.cpf !== '' && !cpfValid}
                  onChange={(event) =>
                    setDoctor({ ...doctor, cpf: event.currentTarget.value })
                  }
                />
                <Form.Control.Feedback type="invalid">
                  Informe um CPF válido.
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group controlId="account-phone">
                <Form.Label>Telefone</Form.Label>
                <Form.Control
                  placeholder="(00) 00000-0000"
                  value={formatPhoneNumber(doctor.phoneNumber)}
                  maxLength={15}
                  isInvalid={doctor.phoneNumber !== '' && !phoneValid}
                  onChange={(event) =>
                    setDoctor({ ...doctor, phoneNumber: event.currentTarget.value })
                  }
                />
                <Form.Control.Feedback type="invalid">
                  Informe um telefone válido.
                </Form.Control.Feedback>
              </Form.Group>
            </>
          )}
        </div>

        <div className={accountCss.actions}>
          <Button type="submit" disabled={!canSave}>
            {saving && <Spinner size="sm" animation="border" />}
            Salvar
          </Button>
        </div>
      </Form>
    </AccountSection>
  )
}

export default ProfileSection
